import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import {AuthContext}  from '../../../components/AuthContext';
import Select from 'react-select';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell
} from 'recharts';
import { 
  Users, UserPlus, Calendar, TrendingUp, Download, 
  Filter, RefreshCw, Eye, FileText, BarChart3, Palette
} from 'lucide-react';

const CRMReports = () => {
  const { user } = useContext(AuthContext);
  
  // State management
  const [dashboardData, setDashboardData] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    companyId: '',
    firmId: '',
    startDate: '',
    endDate: '',
    dateRange: 'all'
  });
  
  // Domain-based filtering states
  const [loadingCompanyInfo, setLoadingCompanyInfo] = useState(false);
  const [hasTriedFetchingCompanies, setHasTriedFetchingCompanies] = useState(false);
  const [availableCompanies, setAvailableCompanies] = useState([]);

  // Create stable API instance
  const api = useMemo(() => {
    const baseURL = process.env.REACT_APP_API_BASE_URL;
    return {
      get: async (endpoint, config = {}) => {
        const url = `${baseURL}${endpoint}`;
        const token = localStorage.getItem('token');
        
        // Build query string
        let queryString = '';
        if (config.params) {
          const params = new URLSearchParams();
          Object.entries(config.params).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
              params.append(key, value);
            }
          });
          queryString = params.toString();
        }
        
        const response = await fetch(`${url}${queryString ? `?${queryString}` : ''}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            ...config.headers
          }
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        return { data: await response.json() };
      }
    };
  }, []);


  // Load companies and firms for selection based on admin access
  useEffect(() => {
    if (!user) return;

    const userData = user.user || user;
    
    // Reset the flag when user changes
    setHasTriedFetchingCompanies(false);
    setAvailableCompanies([]);
    
    console.log('CRMReports: Initializing for Admin user with intersection-based access');
    
    // For Admin users, use intersection-based access (admin assigned + domain matching)
    if (user.role === 'Admin' && userData && !hasTriedFetchingCompanies) {
      fetchAdminCompanies();
    } else if (userData && userData.company) {
      // For other user types with direct company assignment
      fetchCompanies();
    } else {
      // For other cases, fetch all companies
      fetchCompanies();
    }
  }, [user, hasTriedFetchingCompanies]);

  // Handle company selection after companies are fetched
  useEffect(() => {
    if (availableCompanies.length > 0 && !filters.companyId && !filters.firmId) {
      if (availableCompanies.length === 1) {
        setFilters(prev => ({ ...prev, companyId: availableCompanies[0]._id }));
      }
      // Multiple companies case will be handled by the main dashboard loading effect
    }
  }, [availableCompanies, filters.companyId, filters.firmId]);

  // Handle regular companies loading (for non-Admin users) - removed fetchDashboardData call
  // The main dashboard loading effect will handle this case

  const fetchAdminCompanies = async () => {
    if (hasTriedFetchingCompanies || loadingCompanyInfo) {
      console.log('DEBUG Frontend: Skipping fetch - already tried or loading');
      return; // Prevent multiple simultaneous calls
    }
    
    console.log('DEBUG Frontend: Starting to fetch admin companies');
    setLoadingCompanyInfo(true);
    setHasTriedFetchingCompanies(true);
    
    try {
      // Get current domain for filtering company access
      const currentDomain = window.location.hostname;
      
      console.log('DEBUG Frontend: Fetching companies with domain:', currentDomain);
      
      // Fetch companies that are assigned to admin AND match the current domain
      const response = await api.get('/admins/me/companies/combined', {
        params: { domain: currentDomain }
      });
      const adminCompanies = response.data;
      
      console.log('DEBUG Frontend: Received response:', adminCompanies);

      if (adminCompanies && adminCompanies.length > 0) {
        console.log('Received companies from intersection API:', adminCompanies);
        setAvailableCompanies(adminCompanies);
        setCompanies(adminCompanies); // Also set in main companies state
      } else {
        console.warn('No companies returned from intersection API');
        setError('No companies available that match both your admin permissions and current access context. Please contact support.');
      }
    } catch (err) {
      console.error('Error fetching admin companies:', err);
      
      // Provide more specific error messages and fallback options
      if (err.response?.status === 404) {
        setError('Admin not found. Please check your account status or contact support.');
      } else if (err.response?.status === 403) {
        setError('Access denied. You may not have permission to access company information.');
      } else if (err.response?.status === 500) {
        setError('Server error occurred while fetching companies. Please try again later.');
      } else {
        setError('Failed to fetch available companies. Please contact support.');
      }
    } finally {
      setLoadingCompanyInfo(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      if (!user?.user?._id) {
        console.log('Admin ID not available yet');
        return;
      }

      const response = await api.get('/reports/crm/companies', {
        params: { adminId: user.user._id }
      });
      const companyOptions = response.data.data || [];
      
      setCompanies(companyOptions);
    } catch (error) {
      console.error('Error fetching companies:', error);
      setError('Failed to load companies');
    }
  };

  // Handle company/firm selection change
  const handleSelectionChange = (value) => {
    // Check if the selected value is a firm
    const selectedOption = companies.find(option => option._id === value);
    
    if (selectedOption && selectedOption.type === 'firm') {
      // Firm selected
      setFilters(prev => ({
        ...prev,
        firmId: value,
        companyId: ''
      }));
    } else {
      // Company selected or "All Companies"
      setFilters(prev => ({
        ...prev,
        firmId: '',
        companyId: value
      }));
    }
  };

  // Handle date range presets
  const handleDateRangeChange = (range) => {
    const today = new Date();
    let startDate = '';
    let endDate = '';

    switch (range) {
      case 'today':
        startDate = today.toISOString().split('T')[0];
        endDate = today.toISOString().split('T')[0];
        break;
      case 'week':
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - 7);
        startDate = weekStart.toISOString().split('T')[0];
        endDate = new Date().toISOString().split('T')[0];
        break;
      case 'month':
        const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        startDate = monthStart.toISOString().split('T')[0];
        endDate = new Date().toISOString().split('T')[0];
        break;
      case 'quarter':
        const quarterStart = new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3, 1);
        startDate = quarterStart.toISOString().split('T')[0];
        endDate = new Date().toISOString().split('T')[0];
        break;
      case 'year':
        const yearStart = new Date(today.getFullYear(), 0, 1);
        startDate = yearStart.toISOString().split('T')[0];
        endDate = new Date().toISOString().split('T')[0];
        break;
      case 'all':
      default:
        startDate = '';
        endDate = '';
        break;
    }

    setFilters(prev => ({
      ...prev,
      dateRange: range,
      startDate,
      endDate
    }));
  };

  // Get companies for selection with domain-based filtering for Admin users
  const getCompaniesForSelection = useCallback(() => {
    console.log('Getting companies for selection, total companies:', companies.length);
    
    // For Admin users, prioritize availableCompanies from domain-based filtering
    // But only if we have both availableCompanies AND companies (CRM companies) loaded
    if (user?.role === 'Admin' && availableCompanies.length > 0 && companies.length > 0) {
      console.log('Returning domain-filtered companies for Admin:', availableCompanies.length);
      return availableCompanies;
    }
    
    // For other user types or when CRM companies aren't loaded yet, return CRM companies
    if (!companies || companies.length === 0) {
      console.log('No CRM companies available yet');
      return [];
    }
    
    console.log('Returning CRM-filtered companies');
    return companies;
  }, [user?.role, availableCompanies, companies]);

  // Fetch dashboard data
  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Determine parameters based on selection
      const params = {
        startDate: filters.startDate,
        endDate: filters.endDate
      };

      // Add firmId or companyId based on selection
      if (filters.firmId) {
        params.firmId = filters.firmId;
      } else if (filters.companyId) {
        params.companyId = filters.companyId;
      } else {
        // "All Companies" selected - pass all available company IDs
        const availableCompaniesForSelection = getCompaniesForSelection();
        const allCompanyIds = [];
        
        availableCompaniesForSelection.forEach(option => {
          if (option.type === 'firm') {
            // Add all company IDs from this firm
            if (option.companies) {
              option.companies.forEach(company => {
                allCompanyIds.push(company._id);
              });
            }
          } else {
            // Individual company
            allCompanyIds.push(option._id);
          }
        });
        
        if (allCompanyIds.length > 0) {
          params.companyIds = allCompanyIds.join(',');
        } else {
          // Fallback to user's company if no companies available
          params.companyId = user?.user?.company;
        }
      }

      console.log('Fetching CRM dashboard with params:', params);
      const response = await api.get('/reports/crm/dashboard', { params });
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error fetching CRM dashboard:', error);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [api, filters, user, getCompaniesForSelection]);

  // Load data on mount and filter changes
  useEffect(() => {
    // Only fetch dashboard data if:
    // 1. A specific company or firm is selected, OR
    // 2. No specific selection AND companies are loaded (for "All Companies")
    const hasSpecificSelection = filters.companyId || filters.firmId;
    const hasCompaniesLoaded = companies.length > 0;
    
    if (hasSpecificSelection || hasCompaniesLoaded) {
      console.log('Fetching dashboard data:', { hasSpecificSelection, hasCompaniesLoaded, companiesCount: companies.length });
      fetchDashboardData();
    } else {
      console.log('Waiting for companies to load before fetching dashboard data');
    }
  }, [fetchDashboardData, filters.companyId, filters.firmId, companies.length]);

  // Chart colors and utilities
  const CHART_COLORS = {
    primary: ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'],
    gradients: [
      'from-blue-400 to-blue-600',
      'from-green-400 to-green-600', 
      'from-yellow-400 to-yellow-600',
      'from-red-400 to-red-600',
      'from-purple-400 to-purple-600',
      'from-pink-400 to-pink-600',
      'from-cyan-400 to-cyan-600',
      'from-lime-400 to-lime-600'
    ]
  };

  // Prepare pie chart data
  const getPieChartData = () => {
    if (!dashboardData?.summary) return [];
    
    const { totalCustomers, totalDealers, totalReferences, totalGuests } = dashboardData.summary;
    
    return [
      { name: 'Customers', value: totalCustomers || 0, color: CHART_COLORS.primary[0] },
      { name: 'Dealers', value: totalDealers || 0, color: CHART_COLORS.primary[1] },
      { name: 'References', value: totalReferences || 0, color: CHART_COLORS.primary[2] },
      { name: 'Guests', value: totalGuests || 0, color: CHART_COLORS.primary[3] }
    ].filter(item => item.value > 0);
  };

  // React-select custom styles
  const selectStyles = {
    control: (provided, state) => ({
      ...provided,
      borderColor: state.isFocused ? '#3B82F6' : '#D1D5DB',
      boxShadow: state.isFocused ? '0 0 0 1px #3B82F6' : 'none',
      '&:hover': {
        borderColor: '#3B82F6'
      }
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#3B82F6' : state.isFocused ? '#EBF4FF' : 'white',
      color: state.isSelected ? 'white' : '#374151'
    }),
    groupHeading: (provided) => ({
      ...provided,
      backgroundColor: '#F3F4F6',
      color: '#6B7280',
      fontSize: '0.875rem',
      fontWeight: '600',
      textTransform: 'uppercase',
      letterSpacing: '0.025em'
    })
  };

  // Count actual individual companies (not firms)
  const getActualCompanyCount = (companiesForSelection) => {
    let count = 0;
    companiesForSelection.forEach(option => {
      if (option.type === 'firm') {
        // Count all companies within this firm
        count += option.companies ? option.companies.length : 0;
      } else {
        // Individual company
        count += 1;
      }
    });
    return count;
  };

  // Prepare options for react-select
  const getSelectOptions = () => {
    const availableCompaniesForSelection = getCompaniesForSelection();
    const actualCompanyCount = getActualCompanyCount(availableCompaniesForSelection);
    const options = [{ 
      value: '', 
      label: `All Companies (${actualCompanyCount})`, 
      type: 'default' 
    }];
    
    availableCompaniesForSelection.forEach(option => {
      if (option.type === 'firm') {
        // Add firm option
        options.push({
          value: option._id,
          label: `${option.name} - Firm Comparison`,
          type: 'firm',
          isFirm: true
        });
        
        // Add company options under firm
        option.companies.forEach(company => {
          options.push({
            value: company._id,
            label: `${company.name}`,
            type: 'company',
            firmName: option.name
          });
        });
      } else {
        // Individual company
        options.push({
          value: option._id,
          label: `${option.name}${option.hasSeparateCRM ? ' (Separate CRM)' : ''}`,
          type: 'company'
        });
      }
    });
    
    return options;
  };

  // Get date range label
  const getDateRangeLabel = (value) => {
    const labels = {
      'all': 'All Time',
      'today': 'Today', 
      'week': 'Last 7 Days',
      'month': 'This Month',
      'quarter': 'This Quarter',
      'year': 'This Year',
      'custom': 'Custom Range'
    };
    return labels[value] || 'All Time';
  };

  // Export data functions
  const exportData = async (type) => {
    try {
      const companyId = filters.companyId || user?.user?.company?._id || user?.user?.company;
      const token = localStorage.getItem('token');

      const params = new URLSearchParams();
      if (companyId) params.append('companyId', companyId);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/reports/crm/export/${type}/excel?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type}-${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error(`Error exporting ${type}:`, error);
      setError(`Failed to export ${type} data`);
    }
  };

  // Fetch CRM data for dashboard  // Get CSV headers based on data type
  const getCSVHeaders = (type) => {
    switch (type) {
      case 'customers':
        return [
          'Customer Code',
          'Salutation',
          'Name',
          'Phone',
          'Secondary Phone',
          'Email',
          'Station',
          'Pin Code',
          'PAN Number',
          'GST Number',
          'Site Address',
          'Company',
          'Created By',
          'Created Date',
          'Updated Date'
        ];
      case 'dealers':
        return [
          'Dealer ID',
          'Business Name',
          'Business Owner',
          'Phone',
          'Secondary Phone',
          'Email',
          'Manager Name',
          'Address',
          'Station',
          'Pin Code',
          'GST Number',
          'PAN Number',
          'Company',
          'Created By',
          'Created Date',
          'Updated Date'
        ];
      case 'references':
        return [
          'Reference ID',
          'Name',
          'Phone Number',
          'Classification',
          'Type',
          'Station',
          'Company',
          'Created By',
          'Created Date',
          'Updated Date'
        ];
      case 'guests':
        return [
          'Guest ID',
          'Guest Name',
          'Phone Number',
          'Station',
          'Reference',
          'Salesperson',
          'Company',
          'Created By',
          'Created Date'
        ];
      default:
        return [];
    }
  };

  // Get nested object value
  const getNestedValue = (obj, path) => {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  };

  // Get CSV row data based on type
  const getCSVRowData = (item, type) => {
    const formatDate = (date) => {
      return date ? new Date(date).toLocaleDateString() : '';
    };

    switch (type) {
      case 'customers':
        return [
          item.customerCode || '',
          item.salutation || '',
          item.name || '',
          item.phone || '',
          item.secondaryPhone || '',
          item.email || '',
          item.station?.name || '',
          item.pinCode || '',
          item.panNo || '',
          item.gstNo || '',
          item.siteAddress || '',
          item.company?.name || '',
          item.createdBy?.name || '',
          formatDate(item.createdAt),
          formatDate(item.updatedAt)
        ];
      case 'dealers':
        return [
          item.id || '',
          item.businessName || '',
          item.businessOwner || '',
          item.phone || '',
          item.secondaryPhone || '',
          item.email || '',
          item.managerName || '',
          item.address || '',
          item.station?.name || '',
          item.pinCode || '',
          item.gstNo || '',
          item.panNo || '',
          item.company?.name || '',
          item.createdBy?.name || '',
          formatDate(item.createdAt),
          formatDate(item.updatedAt)
        ];
      case 'references':
        return [
          item.id || '',
          item.name || '',
          item.phoneNumber || '',
          item.classification || '',
          item.type || '',
          item.station?.name || '',
          item.company?.name || '',
          item.createdBy?.name || '',
          formatDate(item.createdAt),
          formatDate(item.updatedAt)
        ];
      case 'guests':
        return [
          item.id || '',
          item.guestName || '',
          item.phoneNo || '',
          item.station?.name || '',
          item.reference || '',
          item.salesperson?.name || '',
          item.company?.name || '',
          item.createdBy?.name || '',
          formatDate(item.createdAt)
        ];
      default:
        return [];
    }
  };

  // Prepare chart data
  const prepareChartData = () => {
    if (!dashboardData?.dailyTrends) {
      return [];
    }
    
    const { dailyTrends } = dashboardData;
    const allDates = new Set();
    
    // Collect all dates
    Object.values(dailyTrends).forEach(entityData => {
      entityData.forEach(item => allDates.add(item.date));
    });
    
    const sortedDates = Array.from(allDates).sort();
    
    const chartData = sortedDates.map(date => {
      const dataPoint = { date };
      Object.keys(dailyTrends).forEach(entity => {
        const entityItem = dailyTrends[entity].find(item => item.date === date);
        dataPoint[entity] = entityItem ? entityItem.count : 0;
      });
      return dataPoint;
    });
    
    return chartData;
  };

  const chartData = prepareChartData();

  // Colors for chart lines
  const colors = {
    customers: '#8884d8',
    dealers: '#82ca9d',
    references: '#ffc658',
    guests: '#ff7300'
  };

  if (loadingCompanyInfo) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Retrieving company access information...</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center space-x-2">
          <RefreshCw className="h-5 w-5 animate-spin" />
          <span>Loading CRM reports...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      <div className="p-6 w-full mx-auto">
        {/* Enhanced Header */}
        <div className="mb-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full mb-4">
            <BarChart3 className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mb-2">
            CRM Reports Dashboard
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Comprehensive analytics and insights for customer relationship management data
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-400 text-red-700 rounded-r-lg shadow-sm">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Enhanced Filters */}
        <div className="mb-8 bg-white/70 backdrop-blur-sm p-6 rounded-2xl shadow-xl border border-white/20">
          <h2 className="text-xl font-semibold mb-4 flex items-center text-gray-800">
            <Filter className="h-6 w-6 mr-3 text-blue-600" />
            Filters & Controls
          </h2>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {/* Company Selection with React-Select */}
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700">
              <Users className="inline h-4 w-4 mr-2 text-blue-600" />
              Company / Firm
            </label>
            <Select
              value={getSelectOptions().find(opt => opt.value === (filters.firmId || filters.companyId)) || null}
              onChange={(selected) => handleSelectionChange(selected?.value || '')}
              options={getSelectOptions()}
              styles={selectStyles}
              placeholder="Select company or firm..."
              isClearable
              isSearchable
              className="text-sm"
              formatOptionLabel={(option) => (
                <div className="flex items-center py-1">
                  {option.isFirm && <BarChart3 className="h-4 w-4 mr-2 text-blue-600" />}
                  {option.type === 'company' && !option.isFirm && <Users className="h-4 w-4 mr-2 text-green-600" />}
                  <span className={option.isFirm ? 'font-semibold text-blue-700' : 'text-gray-700'}>
                    {option.label}
                  </span>
                  {option.firmName && (
                    <span className="ml-auto text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                      {option.firmName}
                    </span>
                  )}
                </div>
              )}
            />
          </div>

          {/* Date Range Presets */}
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700">
              <Calendar className="inline h-4 w-4 mr-2 text-blue-600" />
              Date Range
            </label>
            <Select
              value={{ value: filters.dateRange, label: getDateRangeLabel(filters.dateRange) }}
              onChange={(selected) => handleDateRangeChange(selected?.value || 'all')}
              options={[
                { value: 'all', label: 'All Time' },
                { value: 'today', label: 'Today' },
                { value: 'week', label: 'Last 7 Days' },
                { value: 'month', label: 'This Month' },
                { value: 'quarter', label: 'This Quarter' },
                { value: 'year', label: 'This Year' },
                { value: 'custom', label: 'Custom Range' }
              ]}
              styles={selectStyles}
              isSearchable={false}
              className="text-sm"
            />
          </div>

          {/* Custom Date Range */}
          {filters.dateRange === 'custom' && (
            <>
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  Start Date
                </label>
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => setFilters({...filters, startDate: e.target.value})}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  End Date
                </label>
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => setFilters({...filters, endDate: e.target.value})}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                />
              </div>
            </>
          )}

          {/* Refresh Button */}
          <div className="flex items-end">
            <button
              onClick={fetchDashboardData}
              disabled={loading}
              className="w-full px-4 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              <span className="font-semibold">Refresh</span>
            </button>
          </div>
        </div>

        {/* Active Filters Display */}
        {(filters.startDate || filters.endDate || filters.companyId || filters.firmId || filters.dateRange !== 'all') && (
          <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
            <div className="flex flex-wrap gap-3">
              {filters.startDate && filters.endDate && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  <Calendar className="h-4 w-4 mr-1" />
                  {filters.startDate} to {filters.endDate}
                </span>
              )}
              {(filters.companyId || filters.firmId) && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                  <Users className="h-4 w-4 mr-1" />
                  {(() => {
                    if (filters.firmId) {
                      const firm = companies.find(c => c._id === filters.firmId && c.type === 'firm');
                      return firm ? `${firm.name} (Firm)` : 'Selected Firm';
                    } else if (filters.companyId) {
                      for (const option of companies) {
                        if (option.type === 'firm') {
                          const company = option.companies.find(c => c._id === filters.companyId);
                          if (company) return company.name;
                        } else if (option._id === filters.companyId) {
                          return option.name;
                        }
                      }
                      return 'Selected Company';
                    }
                    return '';
                  })()}
                </span>
              )}
              {filters.dateRange !== 'all' && filters.dateRange !== 'custom' && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800 capitalize">
                  <TrendingUp className="h-4 w-4 mr-1" />
                  {filters.dateRange} data
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Summary Cards and Pie Chart */}
      {dashboardData && (
        <div className="grid grid-cols-1 xl:grid-cols-4 gap-6 mb-8">
          {/* Summary Cards */}
          <div className="xl:col-span-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Customers */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-2xl shadow-lg border border-blue-200 hover:shadow-xl transition-all duration-300">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-blue-700 mb-1">Total Customers</p>
                  <p className="text-3xl font-bold text-blue-900">
                    {dashboardData.summary.totalCustomers || 0}
                  </p>
                  <p className="text-xs text-blue-600 mt-2">
                    Contact details & addresses
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="p-3 bg-blue-600 rounded-full">
                    <Users className="h-6 w-6 text-white" />
                  </div>
                  <button
                    onClick={() => exportData('customers')}
                    className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-200 rounded-full transition-colors"
                    title="Export Customers"
                  >
                    <Download className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Dealers */}
            <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-2xl shadow-lg border border-green-200 hover:shadow-xl transition-all duration-300">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-green-700 mb-1">Total Dealers</p>
                  <p className="text-3xl font-bold text-green-900">
                    {dashboardData.summary.totalDealers || 0}
                  </p>
                  <p className="text-xs text-green-600 mt-2">
                    Business partnerships
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="p-3 bg-green-600 rounded-full">
                    <UserPlus className="h-6 w-6 text-white" />
                  </div>
                  <button
                    onClick={() => exportData('dealers')}
                    className="p-2 text-green-600 hover:text-green-800 hover:bg-green-200 rounded-full transition-colors"
                    title="Export Dealers"
                  >
                    <Download className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* References */}
            <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 p-6 rounded-2xl shadow-lg border border-yellow-200 hover:shadow-xl transition-all duration-300">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-yellow-700 mb-1">Total References</p>
                  <p className="text-3xl font-bold text-yellow-900">
                    {dashboardData.summary.totalReferences || 0}
                  </p>
                  <p className="text-xs text-yellow-600 mt-2">
                    Interior & architects
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="p-3 bg-yellow-600 rounded-full">
                    <TrendingUp className="h-6 w-6 text-white" />
                  </div>
                  <button
                    onClick={() => exportData('references')}
                    className="p-2 text-yellow-600 hover:text-yellow-800 hover:bg-yellow-200 rounded-full transition-colors"
                    title="Export References"
                  >
                    <Download className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Guests */}
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-6 rounded-2xl shadow-lg border border-orange-200 hover:shadow-xl transition-all duration-300">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-orange-700 mb-1">Total Guests</p>
                  <p className="text-3xl font-bold text-orange-900">
                    {dashboardData.summary.totalGuests || 0}
                  </p>
                  <p className="text-xs text-orange-600 mt-2">
                    Showroom visitors
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="p-3 bg-orange-600 rounded-full">
                    <Eye className="h-6 w-6 text-white" />
                  </div>
                  <button
                    onClick={() => exportData('guests')}
                    className="p-2 text-orange-600 hover:text-orange-800 hover:bg-orange-200 rounded-full transition-colors"
                    title="Export Guests"
                  >
                    <Download className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Pie Chart */}
          <div className="bg-white/70 backdrop-blur-sm p-6 rounded-2xl shadow-xl border border-white/20">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <Palette className="h-5 w-5 mr-2 text-purple-600" />
              Data Distribution
            </h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={getPieChartData()}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {getPieChartData().map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value, name) => [value.toLocaleString(), name]}
                    contentStyle={{
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      border: 'none',
                      borderRadius: '12px',
                      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)'
                    }}
                  />
                  <Legend 
                    verticalAlign="bottom" 
                    height={36}
                    formatter={(value) => <span className="text-sm font-medium">{value}</span>}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      {dashboardData && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6" style={{display: 'none'}}>
          {/* Customers */}
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Customers</p>
                <p className="text-2xl font-bold text-blue-600">
                  {dashboardData.summary.totalCustomers || 0}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Includes name, contact details, address, PAN/GST info
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <Users className="h-8 w-8 text-blue-600" />
                <button
                  onClick={() => exportData('customers')}
                  className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                  title="Export Customers to Excel - Full details including contact info, address, PAN/GST numbers"
                >
                  <Download className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Dealers */}
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Dealers</p>
                <p className="text-2xl font-bold text-green-600">
                  {dashboardData.summary.totalDealers || 0}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Includes business details, owner info, contact details
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <UserPlus className="h-8 w-8 text-green-600" />
                <button
                  onClick={() => exportData('dealers')}
                  className="p-2 text-gray-400 hover:text-green-600 transition-colors"
                  title="Export Dealers to Excel - Business name, owner details, contact info, GST/PAN numbers"
                >
                  <Download className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>

          {/* References */}
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total References</p>
                <p className="text-2xl font-bold text-yellow-600">
                  {dashboardData.summary.totalReferences || 0}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Includes name, type, classification, contact details
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <TrendingUp className="h-8 w-8 text-yellow-600" />
                <button
                  onClick={() => exportData('references')}
                  className="p-2 text-gray-400 hover:text-yellow-600 transition-colors"
                  title="Export References to Excel - Name, type (Interior/Architect/Designer), classification, contact info"
                >
                  <Download className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Guests */}
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Guests</p>
                <p className="text-2xl font-bold text-orange-600">
                  {dashboardData.summary.totalGuests || 0}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Includes guest details, reference info, salesperson
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <Eye className="h-8 w-8 text-orange-600" />
                <button
                  onClick={() => exportData('guests')}
                  className="p-2 text-gray-400 hover:text-orange-600 transition-colors"
                  title="Export Guests to Excel - Guest name, contact info, reference details, assigned salesperson"
                >
                  <Download className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Company Comparison Table */}
      {dashboardData?.companyComparison && dashboardData.companyComparison.length > 0 && (
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-2xl overflow-hidden mb-8 border border-white/20">
          <div className="px-8 py-6 bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-900 flex items-center">
                <BarChart3 className="h-6 w-6 mr-3 text-indigo-600" />
                Company Performance Comparison
              </h3>
              <div className="flex items-center space-x-3">
                <span className="text-sm text-gray-600">
                  {dashboardData.companyComparison.length} companies analyzed
                </span>
                <div className="h-6 w-px bg-gray-300"></div>
                <button className="text-gray-400 hover:text-gray-600 transition-colors">
                  <Download className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="bg-gradient-to-r from-gray-50 to-gray-100">
                  <th className="px-8 py-4 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">
                    Company Details
                  </th>
                  <th className="px-6 py-4 text-center text-sm font-bold text-blue-700 uppercase tracking-wider">
                    <div className="flex items-center justify-center">
                      <Users className="h-4 w-4 mr-1" />
                      Customers
                    </div>
                  </th>
                  <th className="px-6 py-4 text-center text-sm font-bold text-green-700 uppercase tracking-wider">
                    <div className="flex items-center justify-center">
                      <UserPlus className="h-4 w-4 mr-1" />
                      Dealers
                    </div>
                  </th>
                  <th className="px-6 py-4 text-center text-sm font-bold text-yellow-700 uppercase tracking-wider">
                    <div className="flex items-center justify-center">
                      <TrendingUp className="h-4 w-4 mr-1" />
                      References
                    </div>
                  </th>
                  <th className="px-6 py-4 text-center text-sm font-bold text-orange-700 uppercase tracking-wider">
                    <div className="flex items-center justify-center">
                      <Eye className="h-4 w-4 mr-1" />
                      Guests
                    </div>
                  </th>
                  <th className="px-6 py-4 text-center text-sm font-bold text-purple-700 uppercase tracking-wider">
                    <div className="flex items-center justify-center">
                      <BarChart3 className="h-4 w-4 mr-1" />
                      Total Score
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {dashboardData.companyComparison.map((company, index) => {
                  const total = (company.customers || 0) + (company.dealers || 0) + (company.references || 0) + (company.guests || 0);
                  const isTopPerformer = index < 3;
                  
                  return (
                    <tr 
                      key={company._id} 
                      className={`hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all duration-200 ${
                        index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'
                      } ${isTopPerformer ? 'border-l-4 border-l-yellow-400' : ''}`}
                    >
                      <td className="px-8 py-5">
                        <div className="flex items-center">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg mr-4 ${
                            isTopPerformer 
                              ? 'bg-gradient-to-br from-yellow-400 to-yellow-600' 
                              : 'bg-gradient-to-br from-gray-400 to-gray-600'
                          }`}>
                            {index + 1}
                          </div>
                          <div>
                            <div className="text-base font-bold text-gray-900 mb-1">
                              {company.companyName || company.name || 'Unknown Company'}
                            </div>
                            <div className="flex items-center space-x-2">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                company.type === 'firm' 
                                  ? 'bg-purple-100 text-purple-800' 
                                  : 'bg-blue-100 text-blue-800'
                              }`}>
                                {company.type || 'Company'}
                              </span>
                              {isTopPerformer && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                  ⭐ Top Performer
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-5 text-center">
                        <div className="flex flex-col items-center">
                          <span className="text-2xl font-bold text-blue-600 mb-1">
                            {company.customers || 0}
                          </span>
                          <div className="w-16 bg-blue-100 rounded-full h-2">
                            <div 
                              className="bg-blue-500 h-2 rounded-full transition-all duration-500"
                              style={{ width: `${Math.min(((company.customers || 0) / Math.max(...dashboardData.companyComparison.map(c => c.customers || 0))) * 100, 100)}%` }}
                            ></div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-5 text-center">
                        <div className="flex flex-col items-center">
                          <span className="text-2xl font-bold text-green-600 mb-1">
                            {company.dealers || 0}
                          </span>
                          <div className="w-16 bg-green-100 rounded-full h-2">
                            <div 
                              className="bg-green-500 h-2 rounded-full transition-all duration-500"
                              style={{ width: `${Math.min(((company.dealers || 0) / Math.max(...dashboardData.companyComparison.map(c => c.dealers || 0))) * 100, 100)}%` }}
                            ></div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-5 text-center">
                        <div className="flex flex-col items-center">
                          <span className="text-2xl font-bold text-yellow-600 mb-1">
                            {company.references || 0}
                          </span>
                          <div className="w-16 bg-yellow-100 rounded-full h-2">
                            <div 
                              className="bg-yellow-500 h-2 rounded-full transition-all duration-500"
                              style={{ width: `${Math.min(((company.references || 0) / Math.max(...dashboardData.companyComparison.map(c => c.references || 0))) * 100, 100)}%` }}
                            ></div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-5 text-center">
                        <div className="flex flex-col items-center">
                          <span className="text-2xl font-bold text-orange-600 mb-1">
                            {company.guests || 0}
                          </span>
                          <div className="w-16 bg-orange-100 rounded-full h-2">
                            <div 
                              className="bg-orange-500 h-2 rounded-full transition-all duration-500"
                              style={{ width: `${Math.min(((company.guests || 0) / Math.max(...dashboardData.companyComparison.map(c => c.guests || 0))) * 100, 100)}%` }}
                            ></div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-5 text-center">
                        <div className="flex flex-col items-center">
                          <span className="text-3xl font-bold text-purple-600 mb-1">
                            {total.toLocaleString()}
                          </span>
                          <div className="w-20 bg-purple-100 rounded-full h-3">
                            <div 
                              className="bg-gradient-to-r from-purple-500 to-purple-600 h-3 rounded-full transition-all duration-500"
                              style={{ width: `${Math.min((total / Math.max(...dashboardData.companyComparison.map(c => 
                                (c.customers || 0) + (c.dealers || 0) + (c.references || 0) + (c.guests || 0)
                              ))) * 100, 100)}%` }}
                            ></div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-8 py-4 bg-gradient-to-r from-gray-50 to-gray-100 border-t border-gray-200">
            <div className="flex items-center justify-between text-sm text-gray-600">
              <span>Performance metrics calculated based on total engagement across all categories</span>
              <span className="flex items-center">
                <BarChart3 className="h-4 w-4 mr-1" />
                Updated {new Date().toLocaleDateString()}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Legacy Firm Comparison Table */}
      {dashboardData && dashboardData.type === 'firm' && dashboardData.companyComparisons && (
        <div className="mb-6 bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-semibold mb-4 flex items-center">
            <TrendingUp className="h-5 w-5 mr-2" />
            Company Comparison within {dashboardData.summary.companyName}
          </h2>
          <div className="overflow-x-auto">
            <table className="min-w-full table-auto">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Company
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customers
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Dealers
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    References
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Guests
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {dashboardData.companyComparisons.map((company, index) => (
                  <tr key={company.companyId} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {company.companyName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600 font-semibold">
                      {company.totalCustomers}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-semibold">
                      {company.totalDealers}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-yellow-600 font-semibold">
                      {company.totalReferences}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-orange-600 font-semibold">
                      {company.totalGuests}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Export Information */}
      <div className="mb-6 bg-white p-6 rounded-lg shadow">
        <h2 className="text-lg font-semibold mb-4 flex items-center">
          <FileText className="h-5 w-5 mr-2" />
          Export Information
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-blue-50 p-4 rounded-lg">
            <h3 className="font-semibold text-blue-800 mb-2">Customer Export Fields</h3>
            <p className="text-sm text-blue-700">
              Customer Code, Name, Contact Details, Address, PAN/GST Numbers, Company Info, Creation/Update Info
            </p>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <h3 className="font-semibold text-green-800 mb-2">Dealer Export Fields</h3>
            <p className="text-sm text-green-700">
              Business Name, Owner Details, Contact Info, Manager Details, Address, GST/PAN Numbers, Company Info
            </p>
          </div>
          <div className="bg-yellow-50 p-4 rounded-lg">
            <h3 className="font-semibold text-yellow-800 mb-2">Reference Export Fields</h3>
            <p className="text-sm text-yellow-700">
              Name, Type (Interior/Architect/Designer), Classification, Contact Details, Station Info, Company Info
            </p>
          </div>
          <div className="bg-orange-50 p-4 rounded-lg">
            <h3 className="font-semibold text-orange-800 mb-2">Guest Export Fields</h3>
            <p className="text-sm text-orange-700">
              Guest Name, Contact Info, Reference Details, Salesperson Assignment, Station Info, Company Info
            </p>
          </div>
        </div>
      </div>

      {/* Daily Trends Chart */}
      {dashboardData && chartData.length > 0 && (
        <div className="mb-6 bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-semibold mb-4 flex items-center">
            <BarChart3 className="h-5 w-5 mr-2" />
            Daily Creation Trends
          </h2>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="customers" 
                  stroke={colors.customers} 
                  name="Customers" 
                  strokeWidth={2}
                />
                <Line 
                  type="monotone" 
                  dataKey="dealers" 
                  stroke={colors.dealers} 
                  name="Dealers" 
                  strokeWidth={2}
                />
                <Line 
                  type="monotone" 
                  dataKey="references" 
                  stroke={colors.references} 
                  name="References" 
                  strokeWidth={2}
                />
                <Line 
                  type="monotone" 
                  dataKey="guests" 
                  stroke={colors.guests} 
                  name="Guests" 
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Company Info */}
      {dashboardData && (
        <div className="bg-white p-4 rounded-lg shadow mb-6">
          <h2 className="text-lg font-semibold mb-2">Current View</h2>
          <p className="text-gray-600">
            Showing data for: <span className="font-medium">{dashboardData.summary.companyName}</span>
          </p>
        </div>
      )}

      {/* Recent Creations */}
      {dashboardData?.recentData && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Recent Customers */}
          {dashboardData.recentData.customers.length > 0 && (
            <div className="bg-white p-4 rounded-lg shadow">
              <h3 className="text-md font-semibold mb-3 text-blue-600">Recent Customers</h3>
              <div className="space-y-2">
                {dashboardData.recentData.customers.map((customer, index) => (
                  <div key={index} className="text-sm">
                    <p className="font-medium">{customer.salutation ? `${customer.salutation} ` : ''}{customer.name}</p>
                    <p className="text-gray-500">{customer.phone}</p>
                    <p className="text-xs text-gray-400">{new Date(customer.createdAt).toLocaleDateString()}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recent Dealers */}
          {dashboardData.recentData.dealers.length > 0 && (
            <div className="bg-white p-4 rounded-lg shadow">
              <h3 className="text-md font-semibold mb-3 text-green-600">Recent Dealers</h3>
              <div className="space-y-2">
                {dashboardData.recentData.dealers.map((dealer, index) => (
                  <div key={index} className="text-sm">
                    <p className="font-medium">{dealer.businessName}</p>
                    <p className="text-gray-500">{dealer.businessOwner}</p>
                    <p className="text-gray-500">{dealer.phone}</p>
                    <p className="text-xs text-gray-400">{new Date(dealer.createdAt).toLocaleDateString()}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recent References */}
          {dashboardData.recentData.references.length > 0 && (
            <div className="bg-white p-4 rounded-lg shadow">
              <h3 className="text-md font-semibold mb-3 text-yellow-600">Recent References</h3>
              <div className="space-y-2">
                {dashboardData.recentData.references.map((reference, index) => (
                  <div key={index} className="text-sm">
                    <p className="font-medium">{reference.name}</p>
                    <p className="text-gray-500">{reference.type} - {reference.classification}</p>
                    <p className="text-gray-500">{reference.phoneNumber}</p>
                    <p className="text-xs text-gray-400">{new Date(reference.createdAt).toLocaleDateString()}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recent Guests */}
          {dashboardData.recentData.guests.length > 0 && (
            <div className="bg-white p-4 rounded-lg shadow">
              <h3 className="text-md font-semibold mb-3 text-orange-600">Recent Guests</h3>
              <div className="space-y-2">
                {dashboardData.recentData.guests.map((guest, index) => (
                  <div key={index} className="text-sm">
                    <p className="font-medium">{guest.guestName}</p>
                    <p className="text-gray-500">{guest.phoneNo}</p>
                    <p className="text-gray-500">Ref: {guest.reference}</p>
                    <p className="text-xs text-gray-400">{new Date(guest.createdAt).toLocaleDateString()}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      </div>
    </div>
  );
};

export default CRMReports;
